import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
    useGetDistributorCompanyQuery,
    useGetShareCategoriesQuery,
    useGetShareSubcategoriesQuery,
    useUpdateInvestmentCharacteristicsMutation,
} from 'api/main';
import {
    getEntityIdFromIRI,
    getPaymentMethodText,
    getShareSubcategoryIdFromIRI,
    jsonParseSafe,
    parseDate,
    parseFullName,
    parseSum,
    createCategoryLookup,
} from 'utils/helpers';
import {
    RoleNames,
    entityKey,
    fundTypes,
    investorClassificationKeys,
    roleCategories,
    subscriptionStatus,
} from 'utils/constants';
import {
    useMatchAtLeastOneRole,
    useSubscriptionFormSignatureStatus,
    useUserRoleMatchCategory,
} from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import PartnerDialog from './PartnerDialog';
import ManagerDialog from './ManagerDialog';
import InfoBox from 'containers/distributors/distributor-details/InfoBox';
import InfoRow from 'containers/distributors/distributor-details/InfoRow';
import { ActionButton, CustomSelect, CustomTextField, Loader, ValidationText } from 'components';
import {
    Box,
    IconButton,
    InputAdornment,
    MenuItem,
    FormHelperText,
    Typography,
} from '@mui/material';
import * as icons from 'mdi-material-ui';

const customInputSx = { mb: 0, flex: '0 1 90px' };

// Find category for a subcategory using the lookup map
const findCategoryForSubcategory = (subcategoryId, categoryLookup) => {
    return categoryLookup.get(subcategoryId);
};

// Filter subcategories by category criteria
const filterSubcategoriesByCategory = (subcategories, categoryLookup, filterFn) => {
    return subcategories.filter(subcategory => {
        const category = findCategoryForSubcategory(subcategory.id, categoryLookup);
        return category && filterFn(subcategory, category);
    });
};

function getAdmissionFeeText(admissionFee) {
    return admissionFee === null ? '-' : `${admissionFee} %`;
}

const getInvestorType = (investorClassification, roleName) => {
    const type =
        investorClassification === investorClassificationKeys.PRO
            ? `, ${texts.PRO_CLIENT}`
            : `, ${texts.NON_PRO_CLIENT}`;
    if (roleName.includes('PP')) {
        return texts.PERSONNE_PHYSIQUE + type;
    }
    if (roleName.includes('PM')) {
        return texts.PERSONNE_MORALE + type;
    }

    return '-';
};

const titleSx = { flex: '0 1 33%' };

const getComplyAdvantageStatusText = alert => {
    const info = jsonParseSafe(alert?.additionalInfo);
    const status = [];
    if (info?.pep_user) {
        status.push(texts.PEP_USER);
    }
    if (info?.pep_company) {
        status.push(texts.PEP_COMPANY);
    }
    if (info?.sanction_user) {
        status.push(texts.SANCTIONS_USER);
    }
    if (info?.sanction_company) {
        status.push(texts.SANCTIONS_COMPANY);
    }
    if (info?.pep_co_subscriber) {
        status.push(texts.PEP_CO_SUBSCRIBER);
    }
    if (info?.sanction_co_subscriber) {
        status.push(texts.SANCTIONS_CO_SUBSCRIBER);
    }
    if (status.length === 0) {
        status.push('OK');
    }

    return status.join(' - ');
};

const CompanyOwnerLine = ({ data }) => {
    const { data: company, isLoading: companyLoading } = useGetDistributorCompanyQuery(
        data.distributor_company
    );

    return (
        <Loader loading={companyLoading} marginTop={0} size={13}>
            <Typography>
                {company?.corporateName} - {moment(data.date).format('L')}
            </Typography>
        </Loader>
    );
};

CompanyOwnerLine.propTypes = {
    data: PropTypes.shape({
        distributor_company: PropTypes.number,
        owner: PropTypes.number,
        date: PropTypes.any,
    }),
};

const ProfileSection = ({ subscription, user, setSmsOpen, onSendMessage }) => {
    const mounted = useRef(false);

    const userIsNetworkAdmin = useMatchAtLeastOneRole([RoleNames.NETWORK_ADMIN]);
    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);
    const isAdminOrSignatory = useMatchAtLeastOneRole([
        RoleNames.SERVICER_SIGNATORY,
        RoleNames.SERVICER_ADMIN,
    ]);
    const userIsProvider = useUserRoleMatchCategory(roleCategories.PROVIDERS);
    const userIsPromoterAdmin = useMatchAtLeastOneRole([RoleNames.PROMOTER_ADMIN]);

    const [
        updateInvestmentCharacteristics,
        { isLoading: investmentCharacteristicsLoader, isSuccess: investmentCharacteristicsSuccess },
    ] = useUpdateInvestmentCharacteristicsMutation();
    const { data: shareCategories = [], isLoading: categoriesLoader } =
        useGetShareCategoriesQuery();
    const { data: shareSubcategories = [], isLoading: subcategoriesLoader } =
        useGetShareSubcategoriesQuery();

    const isSubscriptionInProgress = useMemo(
        () => subscription?.subscriptionStatus === subscriptionStatus.IN_PROGRESS,
        [subscription]
    );
    const isSubscriptionFinished = useMemo(
        () => subscription?.subscriptionStatus === subscriptionStatus.FINISHED,
        [subscription]
    );
    const hasSignedBS = useSubscriptionFormSignatureStatus(subscription);

    const admissionFee = useMemo(
        () =>
            typeof subscription?.investmentCharacteristics?.admissionFee === 'undefined' ||
            !subscription?.investmentCharacteristics ||
            (subscription?.indirectUntransferred && !subscription?.transferredToInvestorAt)
                ? null
                : subscription?.investmentCharacteristics?.admissionFee,
        [subscription]
    );

    const investmentSum = useMemo(
        () => subscription?.investmentCharacteristics?.investingSum,
        [subscription]
    );

    const shareSubcategory = useMemo(() => {
        const subcategoryId =
            subscription?.investmentCharacteristics?.shareSubcategory?.id ||
            getShareSubcategoryIdFromIRI(
                subscription?.investmentCharacteristics?.shareSubcategory || '-1'
            );
        return shareSubcategories.find(s => s.id === subcategoryId);
    }, [shareSubcategories, subscription]);

    const canEditAdmissionFee = isAdminOrSignatory && !hasSignedBS && !isSubscriptionFinished;
    const canEditShareSubcategory =
        isAdminOrSignatory && !hasSignedBS && investmentSum && !isSubscriptionFinished;

    // Create category lookup map for efficient category finding
    const categoryLookup = useMemo(() => createCategoryLookup(shareCategories), [shareCategories]);

    // filter categories to match subscription investment fund
    const filteredCategories = useMemo(() => {
        if (!canEditShareSubcategory) return [];
        return shareCategories.filter(
            sc =>
                getEntityIdFromIRI(sc.investmentFund, entityKey.INVESTMENT_FUND) ===
                subscription?.investmentFund.id
        );
    }, [shareCategories, subscription, canEditShareSubcategory]);

    // filter only subcategories that are allowed for editing
    const filteredSubcategories = useMemo(() => {
        if (!canEditShareSubcategory) return []; // prevent unnecessary computations

        const isDirectSubscription = !subscription?.distributorCompany;
        const onlyPrivateAllowed =
            !subscription?.distributorCompany ||
            (!subscription?.distributorCompany?.publicCategoryCommercialization &&
                subscription?.distributorCompany?.privateCategoryCommercialization);
        const onlyPublicAllowed =
            subscription?.distributorCompany?.publicCategoryCommercialization &&
            !subscription?.distributorCompany?.privateCategoryCommercialization;

        const allowedCategories = filteredCategories.filter(fc => {
            if (isDirectSubscription) return true; // return all categories for direct subscriptions
            if (onlyPublicAllowed) return !fc.private;
            if (onlyPrivateAllowed) return fc.private;
            return true;
        });

        // Use optimized filtering with category lookup
        const allowedSubcategories = filterSubcategoriesByCategory(
            shareSubcategories,
            categoryLookup,
            (subcategory, category) => allowedCategories.some(fc => fc.id === category.id)
        );

        const subscribedToFPCI = subscription?.investmentFund?.type === fundTypes.FPCI;
        const subscribedToFPS = subscription?.investmentFund?.type === fundTypes.FPS;

        const investingSum = subscription?.investmentCharacteristics?.investingSum;
        const lowestMinInvestedAmount = Math.min(
            ...allowedSubcategories.map(sc => sc.minInvestedAmount)
        );
        const investedSumIsOutsideInterval =
            investingSum >= 30000 && investingSum < lowestMinInvestedAmount; // this is a special interval for exceptions

        return allowedSubcategories.filter(sc => {
            let subcategoryMatchesSum =
                investingSum >= sc.minInvestedAmount && // invested sum must be higher or equal than min from subcategory
                (sc.maxInvestedAmount ? investingSum <= sc.maxInvestedAmount : true); // invested sum must be lower or equal than max from subcategory
            if (isDirectSubscription) {
                // direct subscriptions (including invites by link)
                // => filter private categories and public that match investment interval
                const ignoreInvestementSumInterval =
                    (subscribedToFPCI || subscribedToFPS) && investedSumIsOutsideInterval;
                return ignoreInvestementSumInterval
                    ? sc.minInvestedAmount === lowestMinInvestedAmount
                    : subcategoryMatchesSum;
            } else {
                // indirect subscriptions
                const ignoreInvestementSumInterval =
                    (subscribedToFPCI || subscribedToFPS) && investedSumIsOutsideInterval;
                return (
                    sc.accessibleToDistribution &&
                    (ignoreInvestementSumInterval
                        ? sc.minInvestedAmount === lowestMinInvestedAmount
                        : subcategoryMatchesSum)
                );
            }
        });
    }, [
        shareSubcategories,
        categoryLookup,
        filteredCategories,
        subscription,
        canEditShareSubcategory,
    ]);

    const complyAdvantageAlert = useMemo(
        () =>
            user.subscriptions[0].userTunnelSteps
                .reduce((acc, crt) => acc.concat(crt.alerts), [])
                .find(
                    a =>
                        (a.message === 'comply_advantage.search_investor' ||
                            a.message === 'comply_advantage.search_co_subscriber') &&
                        a.solved !== true
                ),
        [user]
    );

    const [updateAction, setUpdateAction] = useState('');
    const [newAdmissionFee, setNewAdmissionFee] = useState(admissionFee || '');
    const [admissionFeeError, setAdmissionFeeError] = useState(null);
    const [newSubcategory, setNewSubcategory] = useState(shareSubcategory?.id || '');
    const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);
    const [managerDialogOpen, setManagerDialogOpen] = useState(false);

    const handleUpdateSubcategory = useCallback(() => {
        setUpdateAction('subcategory');
        updateInvestmentCharacteristics({
            id: subscription.investmentCharacteristics.id,
            shareSubcategory: `/api/share_subcategories/${newSubcategory}`,
        });
    }, [updateInvestmentCharacteristics, newSubcategory, subscription]);

    const handleUpdateAdmissionFee = useCallback(() => {
        const maximumAdmissionFee = subscription?.investmentFund?.maximumAdmissionFee || 100;
        if (+newAdmissionFee <= maximumAdmissionFee && +newAdmissionFee >= 0) {
            setUpdateAction('admissionFee');
            updateInvestmentCharacteristics({
                id: subscription.investmentCharacteristics.id,
                admissionFee: +newAdmissionFee,
            });
            setAdmissionFeeError(null);
        } else {
            setAdmissionFeeError(texts.PERCENTAGE_VALUE_CUSTOM(maximumAdmissionFee));
        }
    }, [newAdmissionFee, subscription, updateInvestmentCharacteristics]);

    const handleOpenPartnerDialog = useCallback(() => {
        setPartnerDialogOpen(true);
    }, []);

    const handleOpenManagerDialog = useCallback(() => {
        setManagerDialogOpen(true);
    }, []);

    const handleValidateProStatus = useCallback(() => {
        updateInvestmentCharacteristics({
            id: subscription.investmentCharacteristics.id,
            professionalClientValidated: true,
        });
    }, [updateInvestmentCharacteristics, subscription]);

    const handleInvalidateProStatus = useCallback(() => {
        updateInvestmentCharacteristics({
            id: subscription.investmentCharacteristics.id,
            professionalClientRefused: true,
        });
    }, [updateInvestmentCharacteristics, subscription]);

    useEffect(() => {
        if (investmentCharacteristicsSuccess) {
            setUpdateAction('');
        }
    }, [investmentCharacteristicsSuccess]);

    useEffect(() => {
        if (shareSubcategory?.id) {
            setNewSubcategory(shareSubcategory?.id);
        } else if (filteredSubcategories.length) {
            setNewSubcategory(filteredSubcategories[0].id);
        } else {
            setNewSubcategory('');
        }
    }, [filteredSubcategories, shareSubcategory]);

    useEffect(() => {
        setNewAdmissionFee(admissionFee);
    }, [admissionFee]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    return (
        <InfoBox disableLeftColumn>
            <Box px={4}>
                <Loader loading={categoriesLoader || subcategoriesLoader}>
                    <PartnerDialog
                        open={partnerDialogOpen}
                        currentSubscription={subscription}
                        handleClose={() => setPartnerDialogOpen(false)}
                    />
                    <ManagerDialog
                        open={managerDialogOpen}
                        subscription={subscription}
                        onClose={() => setManagerDialogOpen(false)}
                    />
                    {!!subscription?.distributorCompany && !userIsProvider && (
                        <InfoRow
                            titleSx={titleSx}
                            title={texts.DISTRIBUTOR_SERVICER}
                            data={
                                userIsPromoterAdmin ? (
                                    <Box display="flex" alignItems="center">
                                        {parseFullName(subscription?.internallyManagedBy)}
                                        <IconButton
                                            disableRipple
                                            color="primary"
                                            sx={{
                                                color: 'grey.500',
                                                '&:hover': { color: 'primary.main' },
                                            }}
                                            onClick={handleOpenManagerDialog}
                                        >
                                            <icons.PencilOutline />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    parseFullName(subscription?.internallyManagedBy)
                                )
                            }
                        />
                    )}
                    {!!subscription?.externalDistributorEmail && (
                        <InfoRow
                            titleSx={titleSx}
                            title={texts.EXTERNAL_DISTRIBUTOR_EMAIL}
                            data={subscription?.externalDistributorEmail}
                        />
                    )}
                    <InfoRow
                        titleSx={titleSx}
                        title={texts.PROFIL_INVESTISSEUR}
                        data={
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-start"
                                gap={1}
                            >
                                {getInvestorType(
                                    user.investorClassification,
                                    subscription.role.name
                                )}
                                {isAdminOrSignatory &&
                                    user?.investorClassification ===
                                        investorClassificationKeys.PRO &&
                                    isSubscriptionInProgress && (
                                        <Loader
                                            loading={investmentCharacteristicsLoader}
                                            marginTop={0}
                                        >
                                            {!subscription?.investmentCharacteristics
                                                ?.professionalClientValidated && (
                                                <ActionButton
                                                    disabled={investmentCharacteristicsLoader}
                                                    onClick={handleValidateProStatus}
                                                >
                                                    {texts.VALIDATE_PRO_STATUS}
                                                </ActionButton>
                                            )}
                                            {!subscription?.investmentCharacteristics
                                                ?.professionalClientRefused && (
                                                <ActionButton
                                                    disabled={investmentCharacteristicsLoader}
                                                    onClick={handleInvalidateProStatus}
                                                >
                                                    {texts.INVALIDATE_PRO_STATUS}
                                                </ActionButton>
                                            )}
                                        </Loader>
                                    )}
                                {isAdminOrSignatory &&
                                    !!subscription?.investmentCharacteristics
                                        ?.professionalClientValidated &&
                                    !!subscription.investmentCharacteristics
                                        .professionalClientVerifiedBy && (
                                        <ValidationText
                                            sx={{ margin: 0 }}
                                            validatedAt={
                                                subscription.investmentCharacteristics
                                                    .professionalClientVerifiedAt
                                            }
                                            validatedBy={
                                                subscription.investmentCharacteristics
                                                    .professionalClientVerifiedBy
                                            }
                                        />
                                    )}
                                {isAdminOrSignatory &&
                                    !!subscription?.investmentCharacteristics
                                        ?.professionalClientRefused &&
                                    !!subscription.investmentCharacteristics
                                        .professionalClientVerifiedBy && (
                                        <ValidationText
                                            invalidated
                                            validatedAt={
                                                subscription.investmentCharacteristics
                                                    .professionalClientVerifiedAt
                                            }
                                            validatedBy={
                                                subscription.investmentCharacteristics
                                                    .professionalClientVerifiedBy
                                            }
                                        />
                                    )}
                            </Box>
                        }
                    />
                    {!!user?.investorCompany && (
                        <InfoRow
                            titleSx={titleSx}
                            title={texts.LEGAL_REPRESENTATIVE_LAST_NAME}
                            data={parseFullName(user)}
                        />
                    )}
                    <InfoRow
                        titleSx={titleSx}
                        title={texts.PARTNER}
                        data={
                            <Box display="flex" alignItems="center">
                                {subscription?.distributorCompany?.corporateName ||
                                    subscription?.investmentFund?.sgp?.name +
                                        ` - ${
                                            subscription?.referralCreated ? texts.LINK : texts.SITE
                                        }`}
                                {isAdminOrSignatory && subscription?.distributorCompany && (
                                    <IconButton
                                        disableRipple
                                        color="primary"
                                        sx={{
                                            color: 'grey.500',
                                            '&:hover': { color: 'primary.main' },
                                        }}
                                        onClick={handleOpenPartnerDialog}
                                    >
                                        <icons.PencilOutline />
                                    </IconButton>
                                )}
                            </Box>
                        }
                    />
                    {!!subscription?.createdBy && (
                        <React.Fragment>
                            <InfoRow
                                titleSx={titleSx}
                                title={texts.CONSULTANT}
                                data={parseFullName(subscription?.createdBy)}
                            />
                            {!!subscription?.distributorHistory?.length && (
                                <InfoRow
                                    titleSx={titleSx}
                                    title={texts.TRANSFERRED_AT}
                                    data={subscription?.distributorHistory.map(h => (
                                        <CompanyOwnerLine
                                            key={`${h.distributorCompany}-${h.owner}`}
                                            data={h}
                                        />
                                    ))}
                                />
                            )}
                            <InfoRow
                                titleSx={titleSx}
                                title={texts.CONSULTANT_EMAIL}
                                data={subscription?.createdBy?.email || '-'}
                            />
                            <InfoRow
                                titleSx={titleSx}
                                title={texts.CONSULTANT_PHONE}
                                data={subscription?.createdBy?.phoneNumber || '-'}
                            />
                        </React.Fragment>
                    )}
                    <InfoRow
                        titleSx={titleSx}
                        title={texts.MONTANT_INVESTI}
                        data={parseSum(investmentSum)}
                    />
                    <InfoRow
                        alignItems="center"
                        titleSx={titleSx}
                        title={texts.ADMISSION_FEE}
                        data={
                            canEditAdmissionFee ? (
                                <Box display="flex" alignItems="center" justifyContent="start">
                                    <CustomTextField
                                        formatNumber
                                        formControlProps={{ sx: customInputSx }}
                                        onChange={e => setNewAdmissionFee(e.target.value)}
                                        value={newAdmissionFee}
                                        endAdornment={
                                            <InputAdornment position="start">%</InputAdornment>
                                        }
                                    />
                                    <Loader
                                        loading={
                                            investmentCharacteristicsLoader &&
                                            updateAction === 'admissionFee'
                                        }
                                        mt={0}
                                        size={20}
                                    >
                                        <IconButton
                                            disableRipple
                                            size="small"
                                            color="primary"
                                            disabled={
                                                +admissionFee === +newAdmissionFee ||
                                                !newAdmissionFee
                                            }
                                            onClick={handleUpdateAdmissionFee}
                                        >
                                            <icons.ContentSave />
                                        </IconButton>
                                    </Loader>
                                    {admissionFeeError && (
                                        <FormHelperText error={true}>
                                            {admissionFeeError}
                                        </FormHelperText>
                                    )}
                                </Box>
                            ) : (
                                getAdmissionFeeText(admissionFee)
                            )
                        }
                    />
                    {subscription?.investmentFund?.evergreen && hasSignedBS && (
                        <InfoRow
                            titleSx={titleSx}
                            title={texts.PAYMENT}
                            data={
                                subscription?.subscriptionStatus === subscriptionStatus.FINISHED
                                    ? texts.CONFIRMED
                                    : texts.PENDING
                            }
                        />
                    )}
                    <InfoRow
                        titleSx={titleSx}
                        title={texts.PRODUIT}
                        data={
                            <React.Fragment>
                                {`${subscription?.investmentFund?.name || '-'} / ${
                                    subscription?.investmentFund?.type || '-'
                                } / `}
                                {canEditShareSubcategory ? (
                                    <Box
                                        display="inline-flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        width="120px"
                                    >
                                        <CustomSelect
                                            controlSx={customInputSx}
                                            value={newSubcategory}
                                            onChange={e => setNewSubcategory(e.target.value)}
                                        >
                                            {filteredSubcategories.map(sc => (
                                                <MenuItem key={sc.id} value={sc.id}>
                                                    {sc.name}
                                                </MenuItem>
                                            ))}
                                        </CustomSelect>
                                        <Loader
                                            loading={
                                                investmentCharacteristicsLoader &&
                                                updateAction === 'subcategory'
                                            }
                                            mt={0}
                                            size={20}
                                        >
                                            <IconButton
                                                disableRipple
                                                size="small"
                                                color="primary"
                                                disabled={shareSubcategory?.id === +newSubcategory}
                                                onClick={handleUpdateSubcategory}
                                            >
                                                <icons.ContentSave />
                                            </IconButton>
                                        </Loader>
                                    </Box>
                                ) : (
                                    shareSubcategory?.name || ''
                                )}
                            </React.Fragment>
                        }
                    />
                    <InfoRow
                        titleSx={titleSx}
                        title={texts.PAYMNENT_METHOD}
                        data={getPaymentMethodText(subscription?.bankingStatement?.paymentMethod)}
                    />
                    <InfoRow
                        titleSx={titleSx}
                        title={texts.COMPLY_ADVANTAGE_STATUS}
                        data={getComplyAdvantageStatusText(complyAdvantageAlert)}
                    />
                    {isSubscriptionInProgress && (
                        <InfoRow
                            titleSx={titleSx}
                            title={texts.ETAPE_ACTUELLE}
                            data={subscription?.userFriendlyStatus}
                        />
                    )}
                    <InfoRow
                        clickable={!userIsProvider && !!user.phoneNumber && !userIsStandardServicer}
                        titleSx={titleSx}
                        title={texts.TELEPHONE_NUMBER}
                        data={user.phoneNumber || '-'}
                        onClick={() => setSmsOpen(true)}
                    />
                    <InfoRow
                        titleSx={titleSx}
                        clickable={
                            user.phoneNumber &&
                            !userIsNetworkAdmin &&
                            !userIsProvider &&
                            !userIsStandardServicer
                        }
                        title={texts.E_MAIL}
                        data={user.email || '-'}
                        onClick={onSendMessage}
                    />
                    <InfoRow
                        titleSx={titleSx}
                        title={texts.DATE_ENTREE}
                        data={parseDate(subscription?.createdAt) || '-'}
                    />
                    {!isSubscriptionInProgress && (
                        <InfoRow
                            titleSx={titleSx}
                            title={texts.INVESTMENT_DATE}
                            data={parseDate(subscription?.finishedSubscriptionAt) || '-'}
                        />
                    )}
                    <InfoRow
                        titleSx={titleSx}
                        title={texts.DERNIERE_CONNEXION}
                        data={parseDate(user.lastLoggedAt) || '-'}
                    />
                </Loader>
            </Box>
        </InfoBox>
    );
};

ProfileSection.propTypes = {
    user: PropTypes.object,
    subscription: PropTypes.object,
    setSmsOpen: PropTypes.func,
    onSendMessage: PropTypes.func,
};

export default React.memo(ProfileSection);
